import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, FormControl, Grid, IconButton, InputLabel, Select, TextField, Tooltip, Typography } from "@mui/material";
import { AddCircle as IconAddCircle, IndeterminateCheckBox as IconIndeterminateCheckBox
    , CheckBox as IconCheckBox, Search as IconSearch } from "@mui/icons-material";
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';

import * as periodoService from "../../services/periodoService";
import * as filialService from "../../services/filialService";
import terapiaPeriodoPagoService from "../../services/terapiaPeriodoPagoService";
import DataGridBase from "../../components/common/DataGridBase"
import DialogYesNo from "../../components/DialogYesNo";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Navegabilidad de los pagos de los periodos de terapia: se ven todos los pagos
// (un periodo puede tener mas de uno), con el estado de cada pago y la opcion de
// anular o volver a activar. Para registrar o modificar un pago se entra a la
// pagina de mantenimiento del pago.

// Catalogo 143 = Estados del pago del comprobante.
const ESTADO_PAGO_PENDIENTE = 144;
const ESTADO_PAGO_CANCELADO = 145;
const ESTADO_PAGO_CONSOLIDADO = 227;

// Catalogo 1 = Estados basicos (2 Activo, 4 Anulado).
const ESTADO_ANULADO = 4;

const Index = () => {
    const { isReadOnly } = useMantenimiento("terapiaPeriodoPago");

    const [filters, setFilters] = useState({
        idPeriodo: 0,
        idFilial: 0,
        participante: "",
        serieNumero: "",
        idEstadoPago: 0,
        idEstado: 0,
    });

    const [listPeriodos, setListPeriodos] = useState([]);
    const [listFilial, setListFilial] = useState([]);

    const [cargando, setCargando] = useState(true);
    const [results, setResults] = useState([]);
    const navigate = useNavigate();

    const [openDialogAnnulActive, setOpenDialogAnnulActive] = useState(false);
    const [pagoSeleccionado, setPagoSeleccionado] = useState(null);

    const fetchResults = useCallback(async () => {
        setCargando(true);

        const data = await terapiaPeriodoPagoService.getsTerapiaPeriodoPagoView({
            idPeriodo: filters.idPeriodo == null ? 0 : filters.idPeriodo,
            idFilial: filters.idFilial == null ? 0 : filters.idFilial,
            participante: filters.participante == null ? "" : filters.participante,
            serieNumero: filters.serieNumero == null ? "" : filters.serieNumero,
            idEstadoPago: filters.idEstadoPago == null ? 0 : filters.idEstadoPago,
            idEstado: filters.idEstado == null ? 0 : filters.idEstado,
        })
            .catch((err) => { console.log("Err: ", err) });

        // El grid pide un id unico por fila: el pago se identifica por el periodo
        // de terapia y el numero de pago. El id del periodo se guarda aparte para
        // el enlace a la pagina de mantenimiento.
        setResults((data || []).map((row) => ({
            ...row,
            idTerapiaPeriodo: row.id,
            id: row.id + "-" + row.numero,
        })));
        setCargando(false);
    }, [filters])

    const fetchListPeriodos = useCallback(async () => {
        const data = await periodoService.getsPeriodoView({
            idFilial: 0,
            idTipoTerapia: 0,
            idEstadoApertura: 0,
            mesesHaciaAtras: 0,
            idEstado: 0,
        })
            .catch((err) => { console.log("Err: ", err) });

        setListPeriodos(data || []);
    }, [])

    const fetchListFilial = useCallback(async () => {
        const data = await filialService.getFilialesList()
            .catch((err) => { console.log("Err: ", err) });

        setListFilial(data || []);
    }, [])

    useEffect(() => {
        fetchResults();
        fetchListPeriodos();
        fetchListFilial();
    }, [])

    // Anular o activar el pago elegido.
    const anularActivar = async () => {
        if (!pagoSeleccionado) return;

        // Si ya esta anulado se activa; si esta activo se anula.
        const anulado = pagoSeleccionado.idEstado == ESTADO_ANULADO;

        const llamada = anulado
            ? terapiaPeriodoPagoService.activarTerapiaPeriodoPago(pagoSeleccionado.idTerapiaPeriodo, pagoSeleccionado.numero)
            : terapiaPeriodoPagoService.anularTerapiaPeriodoPago(pagoSeleccionado.idTerapiaPeriodo, pagoSeleccionado.numero);

        await llamada.catch((err) => { console.log("Err: ", err) });

        fetchResults();
    }

    const columns = [
        { field: 'pago', headerName: 'N° Pago', width: 90, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                // El pago se modifica en la pagina de mantenimiento del pago.
                return <Link
                    to={'/therapyperiod/pago/edit/' + cellValues.row.idTerapiaPeriodo + "/" + cellValues.row.numero}
                    style={{ color: 'inherit' }}>
                    {String(cellValues.row.numero).padStart(3, "0")}
                </Link>;
            }
        },
        { field: 'participante', headerName: 'Terapia', width: 200, sortable: false, headerAlign: 'left' },
        { field: 'codigoFilial', headerName: 'Filial', width: 90, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'codigoPeriodo', headerName: 'Periodo', width: 100, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'terapeuta', headerName: 'Terapeuta', width: 160, sortable: false, headerAlign: 'left' },
        { field: 'concepto', headerName: 'Concepto', width: 220, sortable: false, headerAlign: 'left' },
        { field: 'serieNumero', headerName: 'Comprobante', width: 130, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                const row = cellValues.row;

                if (!row.idComprobante || row.idComprobante == 0) return "";

                return <Link to={'/receipt/edit/' + row.idComprobante} style={{ color: 'inherit' }}>
                    {row.serieNumero}
                </Link>;
            }
        },
        { field: 'estadoPago', headerName: 'Estado Pago', width: 130, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'estado', headerName: 'Estado', width: 90, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'monto', headerName: 'Monto', width: 100, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'montoSaldo', headerName: 'Saldo', width: 100, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'observaciones', headerName: 'Observaciones', width: 200, sortable: false, headerAlign: 'left' },
        { field: 'fechaRegistro', headerName: 'Registro', width: 110, type: 'date'
            , valueFormatter: (value) => moment(value).format('D/MM/YYYY')
            , sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'anular', headerName: '', width: 70, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                const row = cellValues.row;
                const anulado = row.idEstado == ESTADO_ANULADO;

                // El boton alterna entre anular y activar.
                return <Tooltip title={anulado ? "Activar pago" : "Anular pago"}>
                    <IconButton aria-label={anulado ? "activar pago" : "anular pago"} size='small'
                        style={{ height: 10 }} align={"center"} disabled={isReadOnly}
                        onClick={() => { setPagoSeleccionado(row); setOpenDialogAnnulActive(true); }}>
                        {anulado ? <IconIndeterminateCheckBox /> : <IconCheckBox />}
                    </IconButton>
                </Tooltip>;
            }
        },
    ];

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Pagos de Periodos de Terapia
                    </Typography>
                </Grid>
                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={12} sm={12}>
                        <Grid container item xs={12} sm={12} spacing={1} padding={0}>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-filialpago">Filial</InputLabel>
                                    <Select
                                        native
                                        label="Filial"
                                        value={filters.idFilial}
                                        onChange={(event) => setFilters({ ...filters, idFilial: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idFilial', id: 'input-filialpago' }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        {listFilial.map((row) => (
                                            <option key={row.id} value={row.id}>{row.descripcion}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-periodopago">Periodo</InputLabel>
                                    <Select
                                        native
                                        label="Periodo"
                                        value={filters.idPeriodo}
                                        onChange={(event) => setFilters({ ...filters, idPeriodo: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idPeriodo', id: 'input-periodopago' }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        {listPeriodos.map((row) => (
                                            <option key={row.id} value={row.id}>{row.codigo}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-estadopagopago">Estado del Pago</InputLabel>
                                    <Select
                                        native
                                        label="Estado del Pago"
                                        value={filters.idEstadoPago}
                                        onChange={(event) => setFilters({ ...filters, idEstadoPago: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idEstadoPago', id: 'input-estadopagopago' }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        <option key={ESTADO_PAGO_PENDIENTE} value={ESTADO_PAGO_PENDIENTE}>{"Pendiente"}</option>
                                        <option key={ESTADO_PAGO_CANCELADO} value={ESTADO_PAGO_CANCELADO}>{"Cancelado"}</option>
                                        <option key={ESTADO_PAGO_CONSOLIDADO} value={ESTADO_PAGO_CONSOLIDADO}>{"Pago Consolidado"}</option>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-estadopago">Estado</InputLabel>
                                    <Select
                                        native
                                        label="Estado"
                                        value={filters.idEstado}
                                        onChange={(event) => setFilters({ ...filters, idEstado: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idEstado', id: 'input-estadopago' }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        <option key={2} value={2}>{"Activo"}</option>
                                        <option key={ESTADO_ANULADO} value={ESTADO_ANULADO}>{"Anulado"}</option>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={8} sm={3}>
                                <TextField
                                    name="participante"
                                    label="Terapia"
                                    value={filters.participante}
                                    fullWidth
                                    onChange={(event) => setFilters({ ...filters, participante: event.target.value })}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            fetchResults();
                                        }
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={8} sm={3}>
                                <TextField
                                    name="serieNumero"
                                    label="Comprobante"
                                    value={filters.serieNumero}
                                    fullWidth
                                    onChange={(event) => setFilters({ ...filters, serieNumero: event.target.value })}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            fetchResults();
                                        }
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                                <Tooltip title={"Buscar pagos"}>
                                    <Button variant="outlined" fullWidth size="medium" onClick={() => { fetchResults() }}>
                                        <IconSearch />
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                                toolbarButtons={[
                                    {
                                        text: "Nuevo",
                                        icon: <IconAddCircle />,
                                        disabled: isReadOnly,
                                        onClick: () => navigate("/therapyperiod/pago/edit/0/0")
                                    }
                                ]}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <DialogYesNo
                open={openDialogAnnulActive}
                titulo={(pagoSeleccionado && pagoSeleccionado.idEstado == ESTADO_ANULADO
                    ? "Activar" : "Anular") + " Pago del Periodo"}
                texto={(pagoSeleccionado && pagoSeleccionado.idEstado == ESTADO_ANULADO)
                    ? "El pago volvera a contar para el periodo de terapia. Desea continuar?"
                    : "El pago dejara de contar para el periodo de terapia, pero no se borrara: se podra activar de nuevo. Desea continuar?"}
                setYes={anularActivar}
                setNo={() => { }}
                openClose={() => setOpenDialogAnnulActive(!openDialogAnnulActive)}
            />
        </div>
    )
}
export default Index;
